import { query, queryAll } from '../lib/query';
import hofEnv from '../lib/environment';

import {
	getCanvasUrl,
	isHomePage,
	isPlayNow,
	redirectExternal,
	redirectHome,
	redirectPlayNow,
} from '../lib/redirect';
import { closeUiDialog, showLoginDialog } from './dialog';
import hofCookie, { QS_PARAMS } from '../lib/cookie-data';
import logging from '../lib/logging';
import HofCanvas from './canvas';

export default class Game {
	/**
	 * @type {HTMLElement[]|undefined}
	 */
	#loginBtn = undefined;

	/**
	 * @type {HTMLBodyElement}
	 */
	#body = document.body;

	/**
	 * @type {HTMLAnchorElement|undefined}
	 */
	#signOutBtn = undefined;

	/**
	 * @type {HTMLElement[]|undefined}
	 */
	#playButtons = undefined;

	#no_user_css_class = 'not-logged';
	#logged_in_user_css_class = 'logged-in';

	#user = undefined;

	/**
	 * @type {string|undefined}
	 */
	#storeUrl = undefined;

	/**
	 * @param {HofUser} user
	 */
	constructor(user) {
		this.#user = user;

		this.#loginBtn = queryAll('[data-login-btn]');

		this.#playButtons = queryAll('.play-button');

		this.#signOutBtn = query('#nav .sign-out a');

		// @TODO: disable for now. Need to check all the cases
		// this.#initCheckRedirectToStore();

		this.#prepare();
		this.#initEventListeners();

		this.init = this.init.bind(this);
	}

	#initCheckRedirectToStore() {
		if (!hofEnv.isMobile) {
			return;
		}

		if (hofEnv.isIOs) {
			if (hofEnv.isValidIOs) {
				return;
			}

			this.#storeUrl =
				'https://apps.apple.com/us/app/house-of-fun-casino-slots/id586634331';
		} else if (hofEnv.isAndroid) {
			if (hofEnv.isValidAndroid) {
				return;
			}

			this.#storeUrl =
				'https://play.google.com/store/apps/details?id=com.pacificinteractive.HouseOfFun';
		}
	}

	#prepare() {
		const isMobile = hofEnv.isMobile;

		if (isMobile) {
			this.#body.classList.add('mobile-mode');
		}

		if (!this.#user.isLoggedIn) {
			this.#body.classList.remove(this.#logged_in_user_css_class);
		} else {
			this.#body.classList.add(this.#logged_in_user_css_class);
			this.#body.classList.remove(this.#no_user_css_class);

			if (!isMobile) {
				redirectPlayNow();
			}
		}
	}

	#initEventListeners() {
		this.#signOutBtn?.addEventListener('click', async (e) => {
			e.preventDefault();

			try {
				await this.#user.logout();
			} catch (e) {
				logging(
					'Sign Out Error',
					() => {
						console.error(e);
					},
					'danger'
				);
			} finally {
				if (isHomePage) {
					window.location.reload();
				} else {
					redirectHome();
				}
			}
		});

		this.#loginBtn?.forEach((element) => {
			element.addEventListener('click', async (e) => {
				e.preventDefault();

				if (typeof this.#storeUrl === 'string') {
					redirectExternal(this.#storeUrl);
					return;
				}

				const loginType = element.dataset?.loginBtn;

				if (!loginType) {
					return;
				}

				closeUiDialog('#dialog');

				const provider = loginType === 'one' ? 'one' : 'facebook';

				if (provider === 'facebook') {
					if (hofEnv.isFBApp) {
						redirectPlayNow();
					} else {
						await this.#login(provider);
					}
				} else if (provider === 'one') {
					await this.#login(provider);
				}
			});
		});

		this.#playButtons?.forEach((button) => {
			button.addEventListener('click', async (e) => {
				e.preventDefault();

				if (typeof this.#storeUrl === 'string') {
					redirectExternal(this.#storeUrl);
					return;
				}

				const dataset = new Map(
					button.dataset ? Object.entries(button.dataset) : undefined
				);

				if (dataset.has('deeplink')) {
					const deeplink = dataset.get('deeplink');

					hofCookie.replace(QS_PARAMS, deeplink);

					const deeplinkTitle = query(
						'.play-deeplink-title',
						button
					)?.textContent?.trim();

					if (deeplinkTitle?.length > 0) {
						const deeplinkText = query('.deeplink-text p span');

						if (deeplinkText) {
							deeplinkText.textContent = deeplinkTitle;

							query('.login-options')?.classList.add('deeplink');
						}
					}
				}

				await this.#playGameInWeb();
			});
		});
	}

	/**
	 * @returns {void}
	 */
	#redirectToHomeIfError() {
		if (!isPlayNow) {
			return;
		}

		redirectHome();
	}

	init() {
		if (HofCanvas.canvas === null) {
			this.#redirectToHomeIfError();
			return;
		}

		if (!this.#user.isLoggedIn) {
			this.#redirectToHomeIfError();

			return;
		}

		this.#playNow();
	}

	async #playGameInWeb() {
		if (this.#user.isDoubleUser) {
			redirectPlayNow();
		} else if (this.#user.isFacebookUser) {
			if (hofEnv.isFBApp) {
				redirectPlayNow();
			} else {
				await this.#login('facebook');
			}
		} else if (this.#user.isOneUser) {
			redirectPlayNow();
		} else {
			showLoginDialog();
		}
	}

	#playNow() {
		try {
			const credentials = this.#user.getCredentials();

			const jsonPayload = JSON.stringify(credentials);

			const canvasUrl = getCanvasUrl({
				credentials: btoa(jsonPayload),
			});

			const canvas = HofCanvas.canvas;

			fetch(canvasUrl, {
				method: 'GET',
				referrerPolicy: 'no-referrer',
				cache: 'no-cache',
			})
				.then((response) => response.text())
				.then((data) => {
					const newDoc =
						canvas?.contentDocument || canvas?.contentWindow?.document;

					if (!newDoc) {
						throw new Error('Iframe document not found');
					}

					newDoc.open();
					newDoc.write(data);
					newDoc.close();

					((document) => {
						const state = document.readyState;

						if (state === 'complete' || state === 'interactive') {
							this.#body.classList.add('iframe-ready');
						} else {
							document.addEventListener('DOMContentLoaded', (e) => {
								this.#body.classList.add('iframe-ready');
							});
						}
					})(newDoc);

					if (canvas?.contentWindow) {
						((window) => {
							const q = canvasUrl.searchParams;

							if (!q.has('deeplink')) {
								q.set('deeplink', '');
							}

							window.hofQueryObjectRaw = q.toString();

							window.hofQueryObject = Object.fromEntries(q);
						})(canvas.contentWindow);
					}

					hofCookie.remove(QS_PARAMS);
				})
				.catch((error) => {
					throw error instanceof Error
						? error
						: new Error('Play Now fetch error');
				});
		} catch (e) {
			logging('Play Now Error', () => {
				console.error(e);
			});
			this.#redirectToHomeIfError();
		}
	}

	/**
	 * @param {THofUserProvider} provider
	 */
	async #login(provider) {
		try {
			const response = await this.#user.login(provider);

			logging(
				'Login Success',
				() => {
					console.log(response);
					console.log(this.#user);
					console.log('provider:', provider);
				},
				'success'
			);

			redirectPlayNow();
		} catch (e) {
			logging(
				'Login Error',
				() => {
					console.log(this.#user);
					console.log('provider:', provider);
					console.error(e);
				},
				'danger'
			);
		}
	}
}
